import { cn } from '@utazzo/ui';
import { Phone, Mail, Building2 } from 'lucide-react';

import { transferRouteLabel } from '../transfer-route';

import type { OpsBookingDetails, OpsCaseItem, OpsFlightSegment } from '@utazzo/shared';

/**
 * The booking facts for one item, laid out so the ops executive can book it
 * from the screen without opening the itinerary: dates and times, who travels,
 * what exactly was sold (room, cabin, vehicle, ticket tier), and who to call.
 */

const fmtDay = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
};
const fmtDur = (min: number | null): string =>
  min === null ? '-' : `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
const title = (s: string | null): string =>
  s
    ? s
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '-';

const Fact = ({
  label,
  children,
  wide,
  strong,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
  strong?: boolean;
}): React.JSX.Element => (
  <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
    <div className="text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">{label}</div>
    <div className={cn('text-sm text-zinc-800', strong && 'font-semibold text-zinc-900')}>
      {children ?? '-'}
    </div>
  </div>
);

const Grid = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div className="grid gap-x-5 gap-y-2.5 rounded-lg border border-zinc-100 bg-zinc-50/70 px-3.5 py-3 sm:grid-cols-3 lg:grid-cols-4">
    {children}
  </div>
);

const paxLine = (p: OpsBookingDetails['pax']): string => {
  const parts = [`${p.adults} adult${p.adults === 1 ? '' : 's'}`];
  if (p.children)
    parts.push(
      `${p.children} child${p.children === 1 ? '' : 'ren'}${p.childAges.length ? ` (${p.childAges.join(', ')} yrs)` : ''}`,
    );
  if (p.infants) parts.push(`${p.infants} infant${p.infants === 1 ? '' : 's'}`);
  return parts.join(' + ');
};

const Segment = ({ s, i }: { s: OpsFlightSegment; i: number }): React.JSX.Element => (
  <div className="grid gap-x-5 gap-y-2 rounded-lg border border-zinc-200 bg-white px-3.5 py-2.5 sm:grid-cols-4">
    <Fact label={`Sector ${i + 1}`} strong>
      {s.origin ?? '?'} → {s.destination ?? '?'}
      <div className="text-xs font-normal text-zinc-500">
        {[s.originCity, s.destinationCity].filter(Boolean).join(' → ')}
      </div>
    </Fact>
    <Fact label="Flight">
      {[
        s.airlineName ?? s.airline,
        s.airline && s.flightNumber ? `${s.airline} ${s.flightNumber}` : s.flightNumber,
      ]
        .filter(Boolean)
        .join(' · ')}
      <div className="text-xs text-zinc-500">{title(s.cabinClass)}</div>
    </Fact>
    <Fact label="Departs">
      {fmtDateTime(s.departureAt)}
      <div className="text-xs text-zinc-500">
        {s.originAirport ?? ''}
        {s.originTerminal ? ` · T${s.originTerminal}` : ''}
      </div>
    </Fact>
    <Fact label="Arrives">
      {fmtDateTime(s.arrivalAt)}
      <div className="text-xs text-zinc-500">
        {s.destinationAirport ?? ''}
        {s.destinationTerminal ? ` · T${s.destinationTerminal}` : ''}
        {s.durationMinutes ? ` · ${fmtDur(s.durationMinutes)}` : ''}
      </div>
    </Fact>
    {s.baggageAllowance || s.cabinBaggage ? (
      <Fact label="Baggage" wide>
        {[
          s.baggageAllowance && `Check-in ${s.baggageAllowance}`,
          s.cabinBaggage && `Cabin ${s.cabinBaggage}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Fact>
    ) : null}
  </div>
);

const SupplierLine = ({ s }: { s: OpsBookingDetails['supplier'] }): React.JSX.Element | null =>
  s ? (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-sky-100 bg-sky-50/60 px-3.5 py-2 text-xs text-sky-900">
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <Building2 className="h-3.5 w-3.5" /> {s.name}
      </span>
      {s.contactName ? <span>{s.contactName}</span> : null}
      {s.contactPhone ? (
        <a
          href={`tel:${s.contactPhone}`}
          className="inline-flex items-center gap-1 hover:underline"
        >
          <Phone className="h-3 w-3" /> {s.contactPhone}
        </a>
      ) : null}
      {s.contactEmail ? (
        <a
          href={`mailto:${s.contactEmail}`}
          className="inline-flex items-center gap-1 hover:underline"
        >
          <Mail className="h-3 w-3" /> {s.contactEmail}
        </a>
      ) : null}
      {s.contractRef ? <span className="text-sky-700">Contract {s.contractRef}</span> : null}
    </div>
  ) : (
    <div className="rounded-lg border border-dashed border-zinc-200 px-3.5 py-2 text-xs text-zinc-500">
      No supplier on the master. Book with the usual vendor and note it on the item.
    </div>
  );

export const BookingDetails = ({ item }: { item: OpsCaseItem }): React.JSX.Element | null => {
  const d = item.details;
  if (!d) return null;
  const pax = paxLine(d.pax);

  const body = ((): React.ReactNode => {
    if (d.flight) {
      const segs = [...d.flight.outbound, ...d.flight.returns];
      return (
        <div className="space-y-2">
          <Grid>
            <Fact label="Passengers" strong>
              {pax}
            </Fact>
            <Fact label="Airline">{d.flight.airlineName ?? '-'}</Fact>
            <Fact label="PNR">{d.pnr ?? (item.issuedReference || '-')}</Fact>
            <Fact label="Travel date">
              {fmtDay(item.startDate ? item.startDate.toISOString() : null)}
            </Fact>
          </Grid>
          {segs.length ? (
            segs.map((s, i) => <Segment key={i} s={s} i={i} />)
          ) : (
            <div className="rounded-lg border border-dashed border-zinc-200 px-3.5 py-2 text-xs text-zinc-500">
              No flight segments on the itinerary (manual flight)
              {item.fromLocation || item.toLocation
                ? ` - route ${item.fromLocation ?? '?'} → ${item.toLocation ?? '?'}`
                : ` - ${item.title}`}
              . Book from the title and the quoted fare.
            </div>
          )}
        </div>
      );
    }
    if (d.hotel) {
      const h = d.hotel;
      return (
        <Grid>
          <Fact label="Hotel" strong wide>
            {h.hotelName}
            {h.starRating ? ` · ${h.starRating}★` : ''}
            <div className="text-xs font-normal text-zinc-500">
              {[h.address, h.city].filter(Boolean).join(', ')}
            </div>
          </Fact>
          <Fact label="Check-in">
            {fmtDay(h.checkIn)}
            {h.checkInTime ? (
              <span className="text-xs text-zinc-500"> from {h.checkInTime}</span>
            ) : null}
          </Fact>
          <Fact label="Check-out">
            {fmtDay(h.checkOut)}
            {h.checkOutTime ? (
              <span className="text-xs text-zinc-500"> by {h.checkOutTime}</span>
            ) : null}
          </Fact>
          <Fact label="Nights">{h.nights ?? '-'}</Fact>
          {/* A package hotel names no room type - the occupancy is the room. */}
          <Fact label="Room" wide>
            {h.roomName ?? h.roomType ?? h.occupancy ?? '-'}
            {h.roomType && h.roomName && h.roomType !== h.roomName ? ` (${h.roomType})` : ''}
          </Fact>
          <Fact label="Meal plan">{title(h.mealPlan)}</Fact>
          <Fact label="Guests" strong>
            {pax}
          </Fact>
          {h.rooms.length ? (
            <Fact label={`Rooms (${h.rooms.length})`} wide>
              <ul className="text-xs text-zinc-700">
                {h.rooms.map((r, i) => (
                  <li key={i}>
                    Room {i + 1}: {r.adults} adult{r.adults === 1 ? '' : 's'}
                    {r.children
                      ? ` + ${r.children} child (${r.childAges.join(', ') || 'age ?'})`
                      : ''}
                    {r.name ? ` · ${r.name}` : ''}
                    {r.mealType ? ` · ${title(r.mealType)}` : ''}
                  </li>
                ))}
              </ul>
            </Fact>
          ) : null}
          {h.occupancy && (h.roomName ?? h.roomType) ? (
            <Fact label="Occupancy">{h.occupancy}</Fact>
          ) : null}
          {h.tboHotelCode ? <Fact label="TBO hotel code">{h.tboHotelCode}</Fact> : null}
          {h.tboBookingCode ? (
            <Fact label="TBO booking code" wide>
              <span className="font-mono text-xs break-all">{h.tboBookingCode}</span>
            </Fact>
          ) : null}
        </Grid>
      );
    }
    if (d.fdSupplement) {
      // A package supplement is a trip-wide extra, not a dated service:
      // show what the package says about it instead of empty route / time.
      const sp = d.fdSupplement;
      const kindLabel =
        sp.kind === 'VEHICLE'
          ? 'Vehicle upgrade'
          : sp.kind === 'VISA'
            ? 'Visa'
            : sp.kind === 'INSURANCE'
              ? 'Insurance'
              : 'Add-on';
      return (
        <Grid>
          <Fact label="Package supplement" strong wide>
            {sp.label}
            {sp.quantity && sp.quantity > 1 ? ` × ${sp.quantity}` : ''}
            <div className="text-xs font-normal text-zinc-500">
              {kindLabel} · priced on the package, for the whole trip
            </div>
          </Fact>
          <Fact label="Travel">
            {fmtDay(sp.travelStart)}
            {sp.travelEnd ? ` - ${fmtDay(sp.travelEnd)}` : ''}
          </Fact>
          <Fact label="Passengers">{pax}</Fact>
          {sp.vehicleType ? (
            <Fact label="Vehicle">
              {sp.vehicleType}
              {sp.quantity && sp.quantity > 1 ? ` × ${sp.quantity}` : ''}
            </Fact>
          ) : null}
          {sp.quantity ? <Fact label="Quantity">{sp.quantity}</Fact> : null}
          {sp.condition ? (
            <Fact label="Applies when" wide>
              {sp.condition}
            </Fact>
          ) : null}
          <Fact label="Package" wide>
            {sp.packageTitle ?? '-'}
            {sp.packageReference ? (
              <span className="text-xs text-zinc-500"> · {sp.packageReference}</span>
            ) : null}
          </Fact>
        </Grid>
      );
    }
    if (d.transfer) {
      const t = d.transfer;
      return (
        <Grid>
          <Fact label="Route" strong wide>
            {transferRouteLabel(item)}
          </Fact>
          <Fact label="Date">{fmtDay(item.startDate ? item.startDate.toISOString() : null)}</Fact>
          <Fact label="Pickup time" strong>
            {t.pickupTime ?? '-'}
          </Fact>
          <Fact label="Passengers">{pax}</Fact>
          <Fact label="Service">
            {[title(t.mode), title(t.serviceType)].filter((x) => x !== '-').join(' · ') || '-'}
          </Fact>
          <Fact label="Vehicle">
            {t.vehicleType ?? '-'}
            {t.vehicleCount && t.vehicleCount > 1 ? ` × ${t.vehicleCount}` : ''}
          </Fact>
          <Fact label="Type">
            {[title(t.category), title(t.transferType)].filter((x) => x !== '-').join(' · ') || '-'}
          </Fact>
          {t.operatorName || t.vesselOrServiceName ? (
            <Fact label="Operator">
              {[t.operatorName, t.vesselOrServiceName].filter(Boolean).join(' · ')}
            </Fact>
          ) : null}
          {t.durationMinutes ? <Fact label="Duration">{fmtDur(t.durationMinutes)}</Fact> : null}
        </Grid>
      );
    }
    if (d.activity) {
      const a = d.activity;
      return (
        <Grid>
          <Fact label="Activity" strong wide>
            {a.activityName}
            {a.variantName ? (
              <div className="text-xs font-normal text-zinc-500">Ticket: {a.variantName}</div>
            ) : null}
          </Fact>
          <Fact label="Date">{fmtDay(item.startDate ? item.startDate.toISOString() : null)}</Fact>
          <Fact label="Time" strong>
            {d.startTime ?? a.pickupTime ?? '-'}
            {d.endTime ? ` - ${d.endTime}` : ''}
          </Fact>
          <Fact label="Participants">{pax}</Fact>
          <Fact label="Transfer">
            {a.transferMode === 'NONE' || !a.transferMode
              ? 'Not included'
              : [
                  title(a.transferMode),
                  a.transferType,
                  a.transferDirection ? title(a.transferDirection) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </Fact>
          <Fact label="Pickup">{a.pickupPoint ?? '-'}</Fact>
          <Fact label="Drop">{a.dropOffPoint ?? '-'}</Fact>
          {a.durationMinutes ? <Fact label="Duration">{fmtDur(a.durationMinutes)}</Fact> : null}
          {a.category ? <Fact label="Category">{a.category}</Fact> : null}
        </Grid>
      );
    }
    if (d.cruise) {
      const c = d.cruise;
      return (
        <Grid>
          <Fact label="Cruise" strong wide>
            {c.shipName}
            {c.cruiseLine ? (
              <div className="text-xs font-normal text-zinc-500">{c.cruiseLine}</div>
            ) : null}
          </Fact>
          <Fact label="Embark">
            {fmtDay(c.checkIn)}
            {c.embarkTime ? <span className="text-xs text-zinc-500"> {c.embarkTime}</span> : null}
          </Fact>
          <Fact label="Disembark">
            {fmtDay(c.checkOut)}
            {c.disembarkTime ? (
              <span className="text-xs text-zinc-500"> {c.disembarkTime}</span>
            ) : null}
          </Fact>
          <Fact label="Cabin" strong>
            {c.cabinName ?? '-'}
            {c.deck ? ` · Deck ${c.deck}` : ''}
          </Fact>
          <Fact label="Nights">{c.nights ?? '-'}</Fact>
          <Fact label="Board basis">{title(c.boardBasis)}</Fact>
          <Fact label="Guests">{pax}</Fact>
          {c.port ? <Fact label="Port">{c.port}</Fact> : null}
        </Grid>
      );
    }
    if (d.visa) {
      const v = d.visa;
      return (
        <Grid>
          <Fact label="Visa" strong wide>
            {v.visaName}
            {v.targetCountry ? (
              <div className="text-xs font-normal text-zinc-500">{v.targetCountry}</div>
            ) : null}
          </Fact>
          <Fact label="Applicants" strong>
            {pax}
          </Fact>
          <Fact label="Type">
            {[title(v.visaType), title(v.entryType)].filter((x) => x !== '-').join(' · ') || '-'}
          </Fact>
          <Fact label="Processing">{title(v.processingMode)}</Fact>
          <Fact label="Validity">
            {v.validityMonths ? `${v.validityMonths} months` : '-'}
            {v.maxStayDays ? ` · stay up to ${v.maxStayDays} days` : ''}
          </Fact>
          <Fact label="Travel from">
            {fmtDay(item.startDate ? item.startDate.toISOString() : null)}
          </Fact>
        </Grid>
      );
    }
    if (d.insurance) {
      const i = d.insurance;
      return (
        <Grid>
          <Fact label="Policy" strong wide>
            {i.name}
          </Fact>
          <Fact label="Insured" strong>
            {pax}
          </Fact>
          <Fact label="Cover">
            {[i.coverageAmountLabel, title(i.coverageScope)]
              .filter((x) => x && x !== '-')
              .join(' · ') || '-'}
          </Fact>
          <Fact label="Type">{title(i.insuranceType)}</Fact>
          <Fact label="Trip dates">
            {fmtDay(item.startDate ? item.startDate.toISOString() : null)}
            {item.endDate ? ` - ${fmtDay(item.endDate.toISOString())}` : ''}
          </Fact>
          {i.maxCoverageDays ? <Fact label="Max days">{i.maxCoverageDays}</Fact> : null}
        </Grid>
      );
    }
    if (d.meal) {
      const m = d.meal;
      return (
        <Grid>
          <Fact label="Meal" strong wide>
            {m.name}
            {m.restaurantName ? (
              <div className="text-xs font-normal text-zinc-500">
                {[m.restaurantName, m.locationName].filter(Boolean).join(' · ')}
              </div>
            ) : null}
          </Fact>
          <Fact label="Date">{fmtDay(item.startDate ? item.startDate.toISOString() : null)}</Fact>
          <Fact label="Time">{d.startTime ?? '-'}</Fact>
          <Fact label="Covers" strong>
            {pax}
          </Fact>
          <Fact label="Kind">{title(m.mealKind)}</Fact>
          {m.cuisine ? <Fact label="Cuisine">{m.cuisine}</Fact> : null}
        </Grid>
      );
    }
    if (d.fixedDeparture) {
      const fd = d.fixedDeparture;
      return (
        <div className="space-y-2">
          <Grid>
            <Fact label="Package" strong wide>
              {fd.packageTitle ?? item.title}
              <div className="text-xs font-normal text-zinc-500">
                {[
                  fd.reference,
                  fd.categoryName,
                  fd.nights != null && fd.days != null ? `${fd.nights}N / ${fd.days}D` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </Fact>
            <Fact label="Travel">
              {fmtDay(item.startDate ? item.startDate.toISOString() : null)}
              {item.endDate ? ` - ${fmtDay(item.endDate.toISOString())}` : ''}
            </Fact>
            <Fact label="Passengers" strong>
              {pax}
            </Fact>
            {fd.supplierName ? <Fact label="Package supplier">{fd.supplierName}</Fact> : null}
          </Grid>
          {fd.hotels.length ? (
            <Grid>
              <Fact label={`Stays (${fd.hotels.length})`} wide>
                <ul className="space-y-1 text-xs text-zinc-700">
                  {fd.hotels.map((h, i) => (
                    <li key={i}>
                      <span className="font-medium text-zinc-900">{h.name}</span>
                      {h.city ? ` · ${h.city}` : ''}
                      {h.checkIn ? ` · ${fmtDay(h.checkIn)}` : ''}
                      {h.checkOut ? ` - ${fmtDay(h.checkOut)}` : ''}
                      {h.nights ? ` · ${h.nights} night${h.nights === 1 ? '' : 's'}` : ''}
                      {h.occupancy ? ` · ${h.occupancy}` : ''}
                      {h.mealPlan ? ` · ${title(h.mealPlan)}` : ''}
                    </li>
                  ))}
                </ul>
              </Fact>
            </Grid>
          ) : null}
          {fd.flights.length ? (
            <div className="space-y-2">
              <div className="text-xs text-zinc-500">
                Flights in this package ({fd.flights.length}) - booked by the Flight desk.
              </div>
              {fd.flights.map((f, fi) =>
                f.segments.length ? (
                  f.segments.map((seg, i) => <Segment key={`${fi}-${i}`} s={seg} i={i} />)
                ) : (
                  <div
                    key={fi}
                    className="rounded-lg border border-dashed border-zinc-200 px-3.5 py-2 text-xs text-zinc-500"
                  >
                    {f.title}
                    {f.date ? ` · ${fmtDay(f.date)}` : ''}
                    {f.block ? ' · block fare' : ''}
                  </div>
                ),
              )}
            </div>
          ) : null}
          {fd.supplements.length ? (
            <Grid>
              <Fact label="Supplements" wide>
                <ul className="text-xs text-zinc-700">
                  {fd.supplements.map((sp, i) => (
                    <li key={i}>
                      {sp.title}
                      {sp.kind ? ` · ${title(sp.kind)}` : ''}
                    </li>
                  ))}
                </ul>
              </Fact>
            </Grid>
          ) : null}
          {fd.inclusions.length ? (
            <Grid>
              <Fact label="Inclusions" wide>
                <ul className="list-disc pl-4 text-xs text-zinc-700">
                  {fd.inclusions.map((text, i) => (
                    <li key={i}>{text}</li>
                  ))}
                </ul>
              </Fact>
            </Grid>
          ) : null}
        </div>
      );
    }
    return (
      <Grid>
        <Fact label="Date">{fmtDay(item.startDate ? item.startDate.toISOString() : null)}</Fact>
        <Fact label="Time">{d.startTime ?? '-'}</Fact>
        <Fact label="Passengers">{pax}</Fact>
      </Grid>
    );
  })();

  return (
    <div className="space-y-2">
      {body}
      <SupplierLine s={d.supplier} />
      {d.supplierRef ? (
        <div className="text-xs text-zinc-500">Supplier ref on itinerary: {d.supplierRef}</div>
      ) : null}
    </div>
  );
};
